import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApiCall from '../../../hooks/useApiCall';
import useErrors from '../../../hooks/useErrors';
import { useQuery } from '../../../hooks/useQuery';
import VtItensService from '../../../services/VtItensService';
import isEmailValid from '../../../utils/isEmailValid';
import { ItensVtEnvelop } from '../types';

export default function useItensRelations() {
  const [isLoading, setIsLoading] = useState(false);
  const [itens, setItens] = useState<ItensVtEnvelop[]>([]);
  const [hasError, setHasError] = useState(false);

  const navigate = useNavigate();

  const query = useQuery();
  const routingId = query.get('routingId');
  const enveloperId = query.get('enveloperId');
  const from = query.get('from');

  const { getErrorMessageByFieldName, errors, setError, removeError } = useErrors();
  const { apiCall } = useApiCall();

  const getPendingItensRelations = useCallback(async () => {
    await apiCall({
      apiToCall: VtItensService.getPendingRelations,
      queryParams: { consultCode: routingId || '' },
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          setHasError(true);
          return;
        }
        setItens((response.itensVTEnvelop as ItensVtEnvelop[] | null)?.filter((x) => !x.cadastrado) || []);
        setHasError(false);
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchAction: () => setHasError(true),
    });
  }, [apiCall, routingId]);

  const handleCorrespondingCodeChange = useCallback((e: ChangeEvent<HTMLInputElement>, itemId: number) => {
    setItens((prev) => {
      const updatedItens = prev.map((item) => {
        if (item.codItem === itemId) {
          return {
            ...item,
            correspondingCode: e.target.value,
          };
        }
        return item;
      });

      const item = updatedItens.find((item) => item.codItem === itemId);
      const isCodeSameOfConfirm = item?.correspondingCode === item?.correspondingCodeConfirm;
      if (!isCodeSameOfConfirm) {
        setError({ field: `${itemId}_confirm`, message: 'Códigos divergentes' });
        setError({ field: `${itemId}`, message: 'Códigos divergentes' });
      } else {
        removeError(`${itemId}_confirm`);
        removeError(`${itemId}`);
      }
      return updatedItens;
    });
  }, [setError, removeError]);

  const handleCorrespondingCodeConfirmChange = useCallback((e: ChangeEvent<HTMLInputElement>, itemId: number) => {
    setItens((prev) => {
      const updatedItens = prev.map((item) => {
        if (item.codItem === itemId) {
          return {
            ...item,
            correspondingCodeConfirm: e.target.value,
          };
        }
        return item;
      });

      const item = updatedItens.find((item) => item.codItem === itemId);
      const isCodeSameOfConfirm = item?.correspondingCode === item?.correspondingCodeConfirm;
      if (!isCodeSameOfConfirm) {
        setError({ field: `${itemId}_confirm`, message: 'Códigos divergentes' });
        setError({ field: `${itemId}`, message: 'Códigos divergentes' });
      } else {
        removeError(`${itemId}_confirm`);
        removeError(`${itemId}`);
      }
      return updatedItens;
    });
  }, [setError, removeError]);

  const isFormValid = useMemo(() => errors.length === 0 && itens.every((item) => !!item.correspondingCode && !!item.correspondingCodeConfirm), [errors, itens]);

  const handleItensRelationsSubmit = useCallback(async () => {
    await apiCall({
      apiToCall: VtItensService.createPendingRelations,
      reqBody: JSON.stringify({
        consulta: Number(routingId),
        email: decodeURIComponent(from || ''),
        codEnvelopadora: Number(enveloperId),
        itensVTEnvelop: itens.map((item) => ({
          codItem: item.codItem,
          codItemEnvelop: item.correspondingCode?.slice(0, 50),
          descrItemEnvelop: item.descrItemVT?.slice(0, 50),
        }))
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: async (response) => {
        if (!response.codigo) {
          toast.error('Erro ao relacionar itens!');
          return;
        }
        toast.success('Itens relacionados com sucesso!');
        await getPendingItensRelations();
      },
    });
  }, [apiCall, enveloperId, from, getPendingItensRelations, itens, routingId]);

  useEffect(() => {
    if (!routingId || !enveloperId || !from || !isEmailValid(decodeURIComponent(from))) {
      toast.error('O link acessado está inválido!');
      navigate('/');
      return;
    }

    getPendingItensRelations();
  }, [enveloperId, from, getPendingItensRelations, navigate, routingId]);

  return {
    isLoading,
    getErrorMessageByFieldName,
    itens,
    handleCorrespondingCodeChange,
    handleCorrespondingCodeConfirmChange,
    isFormValid,
    handleItensRelationsSubmit,
    hasError,
    handleTryAgain: () => getPendingItensRelations(),
  };
}
